import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import { SERVER_URL } from "@/config/config";

import { signMessage } from "@/helper/contractFunctions";
import {
  acceptInvitation,
  approveFamilyContract,
  cancelInvitation,
  createFamily,
  inviteMember,
  kickMember,
  leaveFamily,
  rejectInvitation,
  setSuccessor,
  updateFamilyLeaveFee,
  updateLeader,
} from "@/helper/contractFunctions/MafiaFamily";
import {
  approveFamilyBankContract,
  createStaking,
  withdrawStaking,
} from "@/helper/contractFunctions/MafiaFamilyBank";
import {
  contractRewardsToPromotionObject,
  promotionValuesToSetPayload,
} from "@/constants/familyPromotion";
import {
  depositFamilyGameCash,
  getFamilyPromotionRewards,
  setFamilyPromotionRewards,
  swapFamilyNativeToCash,
  withdrawFamilyGameCash,
  withdrawFamilyNative,
} from "@/helper/contractFunctions/MafiaFamilyCashBank";
import { isIndexingTxError } from "@/helper/parseTxError";

import { BusinessInfo } from "@/types/FamilyBusinessInfo";
import { FamilyDetail } from "@/types/FamilyDetails";
import { FamilyInfo, FamilySummary } from "@/types/FamilyInfo";
import { FamilyInvitation } from "@/types/FamilyInvitation";
import { IFamilyStaking } from "@/types/FamilyStaking";
import InventorySlotInfo from "@/types/InventorySlotInfo";

export interface FamilyState {
  familyList: FamilyInfo[];
  isLoadingFamilyList: boolean;

  familySummaries: FamilySummary[];
  isLoadingFamilySummaries: boolean;

  invitationList: FamilyInvitation[];

  familyStakingInfos: IFamilyStaking[];
  isLoadingFamilyStakingInfos: boolean;

  familyDetail: FamilyDetail;
  familyMapData: {
    familyOwnedTiles: InventorySlotInfo[];
    businessTiles: InventorySlotInfo[];
  };

  isLoadingFamilyDetail: boolean;
  isSigningData: boolean;

  isApprovingFamilyCreate: boolean;
  isPerformingFamilyCreate: boolean;
  isCreatingFamily: boolean;

  isInvitingFamilyMember: boolean;

  isApprovingBankStaking: boolean;
  isPerformingStaking: boolean;
  isCreatingStaking: boolean;
  isWithdrawingStaking: boolean;
  isDepositingFamilyGameCash: boolean;
  isWithdrawingFamilyGameCash: boolean;
  isWithdrawingFamilyNative: boolean;
  isSwappingFamilyNative: boolean;

  isUpdatingLeader: boolean;
  isAcceptingInvitation: boolean;
  isRejectingInvitation: boolean;
  isCancelingInvitation: boolean;

  isLeavingFamily: boolean;
  isKickingMember: boolean;

  isUpdatingFamilyPromotion: boolean;
  isUpdatingFamilyBranding: boolean;
  isUpdatingFamilyLeaveFee: boolean;
  isUpdatingFamilySharing: boolean;
  isUpdatingFamilyCapoRegime: boolean;
}

const initialState: FamilyState = {
  familyList: [],
  isLoadingFamilyList: false,

  familySummaries: [],
  isLoadingFamilySummaries: false,

  familyMapData: {
    familyOwnedTiles: [],
    businessTiles: [],
  },

  invitationList: [],

  familyStakingInfos: [],
  isLoadingFamilyStakingInfos: false,

  familyDetail: {
    members: [],
    capoRegime: [],
    background: null,
    logo: null,
    leaveFee: 0,
    discord: "",
    telegram: "",
    twitter: "",
    topPercent: 100,
    hqSize: 0,

    promotion: null,
    bankInfo: null,
    businessObjects: null,
    vaultPercentInfo: {
      isSuspended: false,
      originalValue: 0,
      value: 0,
    },
  },

  isLoadingFamilyDetail: false,
  isSigningData: false,

  isApprovingFamilyCreate: false,
  isPerformingFamilyCreate: false,
  isCreatingFamily: false,

  isInvitingFamilyMember: false,

  isApprovingBankStaking: false,
  isPerformingStaking: false,
  isCreatingStaking: false,
  isWithdrawingStaking: false,
  isDepositingFamilyGameCash: false,
  isWithdrawingFamilyGameCash: false,
  isWithdrawingFamilyNative: false,
  isSwappingFamilyNative: false,

  isUpdatingLeader: false,
  isAcceptingInvitation: false,
  isRejectingInvitation: false,
  isCancelingInvitation: false,

  isLeavingFamily: false,
  isKickingMember: false,

  isUpdatingFamilyPromotion: false,
  isUpdatingFamilyBranding: false,
  isUpdatingFamilyLeaveFee: false,
  isUpdatingFamilySharing: false,
  isUpdatingFamilyCapoRegime: false,
};

//#region Update family info

export const updateFamilyPromotionAction = createAsyncThunk(
  "family/updateFamilyPromotion",
  async ({
    signMsg,
    signature,
    familyId,
  }: {
    signature: string;
    signMsg: any;
    familyId: number;
  }) => {
    const response = await axios.post(`${SERVER_URL}/family/promotion`, {
      signature,
      signMsg,
      familyId,
    });

    return response;
  }
);

export const updateFamilyBrandingAction = createAsyncThunk(
  "family/updateFamilyBranding",
  async ({
    signMsg,
    signature,
    familyId,
  }: {
    signature: string;
    signMsg: any;
    familyId: number;
  }) => {
    const response = await axios.post(`${SERVER_URL}/family/set`, {
      signature,
      signMsg,
      familyId,
    });

    return response;
  }
);

export const updateCapoRegimeAction = createAsyncThunk(
  "family/updateCapoRegime",
  async ({ signMsg, signature }: { signature: string; signMsg: any }) => {
    const response = await axios.post(`${SERVER_URL}/family/capo`, {
      signature,
      signMsg,
    });

    return response;
  }
);

export const updateFamilySharingAction = createAsyncThunk(
  "family/updateFamilySharing",
  async ({ signMsg, signature }: { signature: string; signMsg: any }) => {
    const response = await axios.post(`${SERVER_URL}/family/share`, {
      signature,
      signMsg,
    });

    return response;
  }
);

//#endregion

//#region Retrieving Family Info

export const getFamilyList = createAsyncThunk(
  "family/getFamilyList",
  async () => {
    const response = await axios.get(`${SERVER_URL}/family`);

    return response.data;
  }
);

export const getFamilyDetailByName = createAsyncThunk(
  "family/getFamilyDetailByName",
  async (familyName: string) => {
    const response = await axios.get(`${SERVER_URL}/family/name/${familyName}`);

    return response.data;
  }
);

export const getFamilyDetailById = createAsyncThunk(
  "family/getFamilyDetailById",
  async (familyId: number) => {
    const response = await axios.get(`${SERVER_URL}/family/id/${familyId}`);

    return response.data;
  }
);

export const getFamilyInvitations = createAsyncThunk(
  "family/getFamilyInvitations",
  async (account: string) => {
    const response = await axios.get(
      `${SERVER_URL}/family/invitation/${account}`
    );

    return response.data;
  }
);

export const getFamilyStakingInfos = createAsyncThunk(
  "family/getFamilyStakingInfos",
  async (account: string) => {
    const response = await axios.get(
      `${SERVER_URL}/profile/staking/${account}`
    );

    return response.data as IFamilyStaking[];
  }
);

export const getFamilyPromotion = createAsyncThunk(
  "family/getFamilyPromotion",
  async (familyId: number) => {
    const rewards = await getFamilyPromotionRewards(familyId);
    return contractRewardsToPromotionObject(rewards);
  }
);

export const setFamilyPromotionRewardsAction = createAsyncThunk(
  "family/setFamilyPromotionRewards",
  async ({
    account,
    familyId,
    promotionValues,
  }: {
    account: string;
    familyId: number;
    promotionValues: Record<string, number | string>;
  }) => {
    const { ranks, amountsCash } = promotionValuesToSetPayload(promotionValues);
    await setFamilyPromotionRewards(account, familyId, ranks, amountsCash);
    return true;
  }
);

export const getFamilyBankInfo = createAsyncThunk(
  "family/getFamilyBankInfo",
  async (familyId: number) => {
    const response = await axios.get(`${SERVER_URL}/family/bank/${familyId}`);

    return response.data;
  }
);

export const getFamilyBusinesses = createAsyncThunk(
  "family/getFamilyBusinesses",
  async (familyId: number) => {
    const response = await axios.get(
      `${SERVER_URL}/family/business/${familyId}`
    );

    return response.data.businessObjects;
  }
);

export const getFamilyMapData = createAsyncThunk(
  "family/getFamilyMapData",
  async (familyId: number) => {
    const response = await axios.get(`${SERVER_URL}/map/family/${familyId}`);

    return response.data;
  }
);

//#endregion

//#region Family contract interaction
export const createFamilyAction = createAsyncThunk(
  "family/createFamily",
  async (account: string) => {
    const data = await createFamily(account);

    return data.transactionHash;
  }
);

export const approveFamilyAction = createAsyncThunk(
  "family/approveFamily",
  async ({
    account,
    approveAmount,
  }: {
    account: string;
    approveAmount: number;
  }) => {
    await approveFamilyContract(account, approveAmount);
  }
);

export const approveBankAction = createAsyncThunk(
  "family/approveBank",
  async ({
    account,
    approveAmount,
  }: {
    account: string;
    approveAmount: number;
  }) => {
    await approveFamilyBankContract(account, approveAmount);
  }
);

export const createBankStakingAction = createAsyncThunk(
  "family/createBankStakingAction",
  async ({
    account,
    familyId,
    amount,
  }: {
    account: string;
    familyId: number;
    amount: number;
  }) => {
    const data = await createStaking(account, familyId, amount);

    return data.transactionHash;
  }
);

export const withdrawBankStakingAction = createAsyncThunk(
  "family/withdrawBankStakingAction",
  async ({ account, stakingId }: { account: string; stakingId: number }) => {
    const data = await withdrawStaking(account, stakingId);

    return data.transactionHash;
  }
);

export const depositFamilyGameCashAction = createAsyncThunk(
  "family/depositFamilyGameCashAction",
  async ({
    account,
    familyId,
    amount,
  }: {
    account: string;
    familyId: number;
    amount: number;
  }) => {
    await depositFamilyGameCash(account, familyId, amount);
    return true;
  }
);

export const withdrawFamilyGameCashAction = createAsyncThunk(
  "family/withdrawFamilyGameCashAction",
  async ({
    account,
    familyId,
    amount,
  }: {
    account: string;
    familyId: number;
    amount: number;
  }) => {
    await withdrawFamilyGameCash(account, familyId, amount);
    return true;
  }
);

export const withdrawFamilyNativeAction = createAsyncThunk(
  "family/withdrawFamilyNativeAction",
  async ({
    account,
    familyId,
    amount,
    recipient,
  }: {
    account: string;
    familyId: number;
    amount: number;
    recipient?: string;
  }) => {
    await withdrawFamilyNative(account, familyId, amount, recipient || account);
    return true;
  }
);

export const swapFamilyNativeToCashAction = createAsyncThunk(
  "family/swapFamilyNativeToCashAction",
  async ({
    account,
    familyId,
    amount,
  }: {
    account: string;
    familyId: number;
    amount: number;
  }) => {
    await swapFamilyNativeToCash(account, familyId, amount);
    return true;
  }
);

export const registerFamilyCreateAction = createAsyncThunk(
  "family/registerFamily",
  async ({ signMsg, signature }: { signMsg: any; signature: string }) => {
    const response = await axios.post(`${SERVER_URL}/family`, {
      signature,
      signMsg,
    });

    return response.data.name;
  }
);

export const inviteFamilyMemberAction = createAsyncThunk(
  "family/inviteFamilyMember",
  async ({
    familyId,
    account,
    invitee,
  }: {
    familyId: number;
    account: string;
    invitee: string;
  }) => {
    const data = await inviteMember(account, familyId, invitee);

    return data.transactionHash;
  }
);

export const acceptFamilyInvitationAction = createAsyncThunk(
  "family/acceptFamilyInvitation",
  async ({
    account,
    invitationId,
  }: {
    account: string;
    invitationId: number;
  }) => {
    const data = await acceptInvitation(account, invitationId);

    return data.transactionHash;
  }
);

export const rejectFamilyInvitationAction = createAsyncThunk(
  "family/rejectFamilyInvitation",
  async ({
    account,
    invitationId,
  }: {
    account: string;
    invitationId: number;
  }) => {
    const data = await rejectInvitation(account, invitationId);

    return data.transactionHash;
  }
);

export const cancelFamilyInvitationAction = createAsyncThunk(
  "family/cancelFamilyInvitation",
  async ({
    account,
    invitationId,
  }: {
    account: string;
    invitationId: number;
  }) => {
    const data = await cancelInvitation(account, invitationId);

    return data.transactionHash;
  }
);

export const updateFamilyLeaderAction = createAsyncThunk(
  "family/updateFamilyLeader",
  async ({
    account,
    familyId,
    memberAddress,
    leaderIndex,
  }: {
    account: string;
    familyId: number;
    memberAddress: string;
    leaderIndex: number;
  }) => {
    const data = await updateLeader(
      account,
      familyId,
      memberAddress,
      leaderIndex
    );

    return data.transactionHash;
  }
);

export const updateFamilySuccessorAction = createAsyncThunk(
  "family/updateFamilySuccessor",
  async ({
    account,
    familyId,
    successor,
  }: {
    account: string;
    familyId: number;
    successor: string;
  }) => {
    const data = await setSuccessor(account, familyId, successor);

    return data.transactionHash;
  }
);

export const leaveFamilyAction = createAsyncThunk(
  "family/leaveFamily",
  async ({ account, familyId }: { account: string; familyId: number }) => {
    const data = await leaveFamily(account, familyId);

    return data.transactionHash;
  }
);

export const kickMemberAction = createAsyncThunk(
  "family/kickMemberAction",
  async ({
    account,
    familyId,
    memberAddress,
  }: {
    account: string;
    familyId: number;
    memberAddress: string;
  }) => {
    const data = await kickMember(account, familyId, memberAddress);

    return data.transactionHash;
  }
);

export const updateFamilyLeaveFeeAction = createAsyncThunk(
  "family/updateFamilyLeaveFeeAction",
  async ({
    account,
    familyId,
    value,
  }: {
    account: string;
    familyId: number;
    value: number;
  }) => {
    const data = await updateFamilyLeaveFee(account, familyId, value);

    return data.transactionHash;
  }
);

//#endregion

//#region Sign message
export const signFamilyCreateAction = createAsyncThunk(
  "family/signFamily",
  async ({ signMsg }: { signMsg: any }) => {
    const signature = await signMessage(signMsg);

    return signature;
  }
);

export const signData = createAsyncThunk(
  "family/signData",
  async ({ signMsg }: { signMsg: any }) => {
    const signature = await signMessage(signMsg);

    return signature;
  }
);

//#endregion

export const familySlice = createSlice({
  name: "family",
  initialState,
  reducers: {
    setFamilyLeaveFee: (state, { payload }) => {
      state.familyDetail = {
        ...state.familyDetail,
        leaveFee: payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getFamilyList.pending, (state) => {
      state.isLoadingFamilyList = true;
    });
    builder.addCase(getFamilyList.fulfilled, (state, { payload }) => {
      state.isLoadingFamilyList = false;
      state.familyList = payload.map((family: any) => {
        return {
          ...family,
        } as FamilyInfo;
      });

      state.familySummaries = payload.map((item: any) => ({
        id: item.id,
        name: item.name,
        stMafia: item.stMafia,
        cityId: item.city,
        logo: item.logo,
      }));
    });
    builder.addCase(getFamilyList.rejected, (state, { error }) => {
      state.isLoadingFamilyList = false;
    });

    builder.addCase(getFamilyDetailByName.pending, (state) => {
      state.isLoadingFamilyDetail = true;
    });
    builder.addCase(getFamilyDetailByName.fulfilled, (state, { payload }) => {
      state.familyDetail = { ...state.familyDetail, ...payload };

      state.familyDetail = {
        ...payload,
        hqSize: payload.size,
        leaveFee: state.familyDetail.leaveFee,
        promotion: state.familyDetail.promotion,
        bankInfo: state.familyDetail.bankInfo,
        businessObjects: state.familyDetail.businessObjects,
      };
      state.isLoadingFamilyDetail = false;
    });
    builder.addCase(getFamilyDetailByName.rejected, (state, { error }) => {
      state.isLoadingFamilyDetail = false;
    });

    builder.addCase(getFamilyDetailById.pending, (state) => {});
    builder.addCase(getFamilyDetailById.fulfilled, (state, { payload }) => {});
    builder.addCase(getFamilyDetailById.rejected, (state, { error }) => {});

    builder.addCase(getFamilyInvitations.pending, () => {});
    builder.addCase(getFamilyInvitations.fulfilled, (state, { payload }) => {
      state.invitationList = payload;
    });
    builder.addCase(getFamilyInvitations.rejected, (state, { error }) => {});

    builder.addCase(getFamilyStakingInfos.pending, (state) => {
      state.isLoadingFamilyStakingInfos = true;
    });
    builder.addCase(getFamilyStakingInfos.fulfilled, (state, { payload }) => {
      state.familyStakingInfos = payload;
      state.isLoadingFamilyStakingInfos = false;
    });
    builder.addCase(getFamilyStakingInfos.rejected, (state) => {
      state.isLoadingFamilyStakingInfos = false;
    });

    builder.addCase(getFamilyPromotion.pending, () => {});
    builder.addCase(getFamilyPromotion.fulfilled, (state, { payload }) => {
      state.familyDetail = {
        ...state.familyDetail,
        promotion: payload,
      };
    });
    builder.addCase(getFamilyPromotion.rejected, (state, { error }) => {
      state.familyDetail = {
        ...state.familyDetail,
        promotion: null,
      };
    });

    builder.addCase(getFamilyBankInfo.pending, (state) => {});
    builder.addCase(getFamilyBankInfo.fulfilled, (state, { payload }) => {
      state.familyDetail = {
        ...state.familyDetail,
        bankInfo: payload,
      };
    });
    builder.addCase(getFamilyBankInfo.rejected, (state, { error }) => {
      state.familyDetail = {
        ...state.familyDetail,
        bankInfo: null,
      };
    });

    builder.addCase(getFamilyBusinesses.pending, (state) => {});
    builder.addCase(getFamilyBusinesses.fulfilled, (state, { payload }) => {
      const familyBusiness: BusinessInfo[] = payload.map(
        (business: any, index: number) => {
          return {
            id: business.id,
            userId: business.user_id,
            itemId: business.item_id,
            city: business.city,
            owner: business.owner,
            itemDetail: {
              id: business.itemDetail.id,
              typeId: business.itemDetail.type_id,
              categoryId: business.itemDetail.category_id,
              categoryName: business.itemDetail.category_name,
              typeName: business.itemDetail.type_name,
              inventoryWorth: business.itemDetail.inventory_worth,
            },
          };
        }
      );

      state.familyDetail = {
        ...state.familyDetail,
        businessObjects: familyBusiness,
      };
    });
    builder.addCase(getFamilyBusinesses.rejected, (state, { error }) => {
      state.familyDetail = {
        ...state.familyDetail,
        businessObjects: null,
      };
    });

    builder.addCase(updateFamilyPromotionAction.pending, (state) => {
      state.isUpdatingFamilyPromotion = true;
    });
    builder.addCase(
      updateFamilyPromotionAction.fulfilled,
      (state, { payload }) => {
        state.isUpdatingFamilyPromotion = false;
      }
    );
    builder.addCase(
      updateFamilyPromotionAction.rejected,
      (state, { error }) => {
        state.isUpdatingFamilyPromotion = false;
      }
    );

    builder.addCase(setFamilyPromotionRewardsAction.pending, (state) => {
      state.isUpdatingFamilyPromotion = true;
    });
    builder.addCase(setFamilyPromotionRewardsAction.fulfilled, (state) => {
      state.isUpdatingFamilyPromotion = false;
    });
    builder.addCase(setFamilyPromotionRewardsAction.rejected, (state) => {
      state.isUpdatingFamilyPromotion = false;
    });

    builder.addCase(updateFamilyBrandingAction.pending, (state) => {
      state.isUpdatingFamilyBranding = true;
    });

    builder.addCase(
      updateFamilyBrandingAction.fulfilled,
      (state, { payload }) => {
        state.isUpdatingFamilyBranding = false;
      }
    );
    builder.addCase(updateFamilyBrandingAction.rejected, (state, { error }) => {
      state.isUpdatingFamilyBranding = false;
    });

    builder.addCase(updateFamilySharingAction.pending, (state) => {
      state.isUpdatingFamilySharing = true;
    });

    builder.addCase(
      updateFamilySharingAction.fulfilled,
      (state, { payload }) => {
        state.isUpdatingFamilySharing = false;
      }
    );
    builder.addCase(updateFamilySharingAction.rejected, (state, { error }) => {
      state.isUpdatingFamilySharing = false;
    });

    builder.addCase(updateCapoRegimeAction.pending, (state) => {
      state.isUpdatingFamilyCapoRegime = true;
    });

    builder.addCase(updateCapoRegimeAction.fulfilled, (state, { payload }) => {
      state.isUpdatingFamilyCapoRegime = false;
    });
    builder.addCase(updateCapoRegimeAction.rejected, (state, { error }) => {
      state.isUpdatingFamilyCapoRegime = false;
    });

    builder.addCase(approveFamilyAction.pending, (state) => {
      state.isApprovingFamilyCreate = true;
      state.isCreatingFamily = true;
    });
    builder.addCase(approveFamilyAction.fulfilled, (state, { payload }) => {
      state.isApprovingFamilyCreate = false;
    });
    builder.addCase(approveFamilyAction.rejected, (state, { error }) => {
      state.isApprovingFamilyCreate = false;
      state.isCreatingFamily = false;
    });

    builder.addCase(createFamilyAction.pending, (state) => {
      state.isPerformingFamilyCreate = true;
      state.isCreatingFamily = true;
    });
    builder.addCase(createFamilyAction.fulfilled, (state, { payload }) => {
      state.isPerformingFamilyCreate = false;
    });
    builder.addCase(createFamilyAction.rejected, (state, { error }) => {
      state.isPerformingFamilyCreate = false;
      state.isCreatingFamily = false;
    });

    builder.addCase(signFamilyCreateAction.pending, (state) => {
      state.isCreatingFamily = true;
    });

    builder.addCase(
      signFamilyCreateAction.fulfilled,
      (state, { payload }) => {}
    );
    builder.addCase(signFamilyCreateAction.rejected, (state, { error }) => {
      state.isCreatingFamily = false;
    });

    builder.addCase(registerFamilyCreateAction.pending, (state) => {
      state.isCreatingFamily = true;
    });
    builder.addCase(
      registerFamilyCreateAction.fulfilled,
      (state, { payload }) => {
        state.isCreatingFamily = false;
      }
    );
    builder.addCase(registerFamilyCreateAction.rejected, (state, { error }) => {
      state.isCreatingFamily = false;
    });

    builder.addCase(inviteFamilyMemberAction.pending, (state) => {
      state.isInvitingFamilyMember = true;
    });
    builder.addCase(
      inviteFamilyMemberAction.fulfilled,
      (state, { payload }) => {
        state.isInvitingFamilyMember = false;
      }
    );
    builder.addCase(inviteFamilyMemberAction.rejected, (state, { error }) => {
      state.isInvitingFamilyMember = false;
    });

    builder.addCase(approveBankAction.pending, (state) => {
      state.isApprovingBankStaking = true;
      state.isCreatingStaking = true;
    });
    builder.addCase(approveBankAction.fulfilled, (state, { payload }) => {
      state.isApprovingBankStaking = false;
    });
    builder.addCase(approveBankAction.rejected, (state, { error }) => {
      state.isApprovingBankStaking = false;

      if (!isIndexingTxError(error))
        state.isCreatingStaking = false;
    });

    builder.addCase(createBankStakingAction.pending, (state) => {
      state.isPerformingStaking = true;
    });
    builder.addCase(createBankStakingAction.fulfilled, (state, { payload }) => {
      state.isPerformingStaking = false;
    });
    builder.addCase(createBankStakingAction.rejected, (state, { error }) => {
      state.isPerformingStaking = false;
      state.isCreatingStaking = false;
    });

    builder.addCase(withdrawBankStakingAction.pending, (state) => {
      state.isWithdrawingStaking = true;
    });
    builder.addCase(
      withdrawBankStakingAction.fulfilled,
      (state, { payload }) => {
        state.isWithdrawingStaking = false;
      }
    );
    builder.addCase(withdrawBankStakingAction.rejected, (state, { error }) => {
      state.isWithdrawingStaking = false;
    });

    builder.addCase(depositFamilyGameCashAction.pending, (state) => {
      state.isDepositingFamilyGameCash = true;
    });
    builder.addCase(depositFamilyGameCashAction.fulfilled, (state) => {
      state.isDepositingFamilyGameCash = false;
    });
    builder.addCase(depositFamilyGameCashAction.rejected, (state) => {
      state.isDepositingFamilyGameCash = false;
    });

    builder.addCase(withdrawFamilyGameCashAction.pending, (state) => {
      state.isWithdrawingFamilyGameCash = true;
    });
    builder.addCase(withdrawFamilyGameCashAction.fulfilled, (state) => {
      state.isWithdrawingFamilyGameCash = false;
    });
    builder.addCase(withdrawFamilyGameCashAction.rejected, (state) => {
      state.isWithdrawingFamilyGameCash = false;
    });

    builder.addCase(withdrawFamilyNativeAction.pending, (state) => {
      state.isWithdrawingFamilyNative = true;
    });
    builder.addCase(withdrawFamilyNativeAction.fulfilled, (state) => {
      state.isWithdrawingFamilyNative = false;
    });
    builder.addCase(withdrawFamilyNativeAction.rejected, (state) => {
      state.isWithdrawingFamilyNative = false;
    });

    builder.addCase(swapFamilyNativeToCashAction.pending, (state) => {
      state.isSwappingFamilyNative = true;
    });
    builder.addCase(swapFamilyNativeToCashAction.fulfilled, (state) => {
      state.isSwappingFamilyNative = false;
    });
    builder.addCase(swapFamilyNativeToCashAction.rejected, (state) => {
      state.isSwappingFamilyNative = false;
    });

    builder.addCase(updateFamilyLeaderAction.pending, (state) => {
      state.isUpdatingLeader = true;
    });
    builder.addCase(
      updateFamilyLeaderAction.fulfilled,
      (state, { payload }) => {
        state.isUpdatingLeader = false;
      }
    );
    builder.addCase(updateFamilyLeaderAction.rejected, (state, { error }) => {
      state.isUpdatingLeader = false;
    });

    builder.addCase(updateFamilySuccessorAction.pending, (state) => {
      state.isUpdatingLeader = true;
    });
    builder.addCase(
      updateFamilySuccessorAction.fulfilled,
      (state, { payload }) => {
        state.isUpdatingLeader = false;
      }
    );
    builder.addCase(
      updateFamilySuccessorAction.rejected,
      (state, { error }) => {
        state.isUpdatingLeader = false;
      }
    );

    builder.addCase(acceptFamilyInvitationAction.pending, (state) => {
      state.isAcceptingInvitation = true;
    });
    builder.addCase(
      acceptFamilyInvitationAction.fulfilled,
      (state, { payload }) => {
        state.isAcceptingInvitation = false;
      }
    );
    builder.addCase(
      acceptFamilyInvitationAction.rejected,
      (state, { error }) => {
        state.isAcceptingInvitation = false;
      }
    );

    builder.addCase(cancelFamilyInvitationAction.pending, (state) => {
      state.isCancelingInvitation = true;
    });
    builder.addCase(
      cancelFamilyInvitationAction.fulfilled,
      (state, { payload }) => {
        state.isCancelingInvitation = false;
      }
    );
    builder.addCase(
      cancelFamilyInvitationAction.rejected,
      (state, { error }) => {
        state.isCancelingInvitation = false;
      }
    );

    builder.addCase(rejectFamilyInvitationAction.pending, (state) => {
      state.isRejectingInvitation = true;
    });
    builder.addCase(
      rejectFamilyInvitationAction.fulfilled,
      (state, { payload }) => {
        state.isRejectingInvitation = false;
      }
    );
    builder.addCase(
      rejectFamilyInvitationAction.rejected,
      (state, { error }) => {
        state.isRejectingInvitation = false;
      }
    );

    builder.addCase(leaveFamilyAction.pending, (state) => {
      state.isLeavingFamily = true;
    });
    builder.addCase(leaveFamilyAction.fulfilled, (state, { payload }) => {
      state.isLeavingFamily = false;
    });
    builder.addCase(leaveFamilyAction.rejected, (state, { error }) => {
      state.isLeavingFamily = false;
    });

    builder.addCase(kickMemberAction.pending, (state) => {
      state.isKickingMember = true;
    });
    builder.addCase(kickMemberAction.fulfilled, (state, { payload }) => {
      state.isKickingMember = false;
    });
    builder.addCase(kickMemberAction.rejected, (state, { error }) => {
      state.isKickingMember = false;
    });

    builder.addCase(updateFamilyLeaveFeeAction.pending, (state) => {
      state.isUpdatingFamilyLeaveFee = true;
    });
    builder.addCase(
      updateFamilyLeaveFeeAction.fulfilled,
      (state, { payload }) => {
        state.isUpdatingFamilyLeaveFee = false;
      }
    );
    builder.addCase(updateFamilyLeaveFeeAction.rejected, (state, { error }) => {
      state.isUpdatingFamilyLeaveFee = false;
    });

    builder.addCase(signData.pending, (state) => {
      state.isSigningData = true;
    });
    builder.addCase(signData.fulfilled, (state, { payload }) => {
      state.isSigningData = false;
    });
    builder.addCase(signData.rejected, (state, { error }) => {
      state.isSigningData = false;
    });

    builder.addCase(getFamilyMapData.pending, (state) => {});
    builder.addCase(getFamilyMapData.fulfilled, (state, { payload }) => {
      state.familyMapData = payload as {
        familyOwnedTiles: InventorySlotInfo[];
        businessTiles: InventorySlotInfo[];
      };
    });
    builder.addCase(getFamilyMapData.rejected, (state, { error }) => {});
  },
});

export const { setFamilyLeaveFee } = familySlice.actions;

export default familySlice.reducer;
